<template>
  <div id="admin-stripe-payments">
    <v-card class="px-5 py-2 mt-4 mb-5 elevation-0">
      <v-row class="align-end">
        <v-col cols="12" md="3" class="my-0 py-0">
          <v-text-field
            v-model="filters.dateFrom"
            label="Fecha desde"
            type="date"
            :disabled="loading"
            hide-details
            clearable
            @keydown.enter="updateFilters"
          />
        </v-col>
        <v-col cols="12" md="3" class="my-0 py-0">
          <v-text-field
            v-model="filters.dateTo"
            label="Fecha hasta"
            type="date"
            :disabled="loading"
            hide-details
            clearable
            @keydown.enter="updateFilters"
          />
        </v-col>
        <v-col cols="12" md="2" class="my-0 py-0">
          <v-text-field
            v-model="filters.userReferralCode"
            label="Código referral"
            :disabled="loading"
            hide-details
            clearable
            @keydown.enter="updateFilters"
          />
        </v-col>
        <v-col cols="12" md="2" class="my-0 py-0">
          <v-text-field
            v-model="filters.referrerUserId"
            label="referrerUserId"
            :disabled="loading"
            hide-details
            clearable
            @keydown.enter="updateFilters"
          />
        </v-col>
        <v-col cols="12" md="2" class="my-0 py-0 d-flex align-center">
          <v-btn color="primary" small class="mt-4" :loading="loading" @click="updateFilters">
            Buscar
          </v-btn>
          <ExportButton
            class="mt-4"
            :click="downloadCsv"
            :loading="exportLoading"
            :disabled="loading"
          />
        </v-col>
      </v-row>
    </v-card>

    <v-card class="px-5 py-2 mt-3 mb-5 elevation-0">
      <v-data-table
        :headers="headers"
        :items="tableItems"
        :options.sync="options"
        :server-items-length="totalItems"
        sort-by="date"
        :sort-desc="true"
        class="admin-stripe-payments-table elevation-0 mt-2"
        mobile-breakpoint="0"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': defaultPaginationItemsPerPageOptions,
        }"
        :items-per-page="20"
        dense
        :item-class="getItemClass"
        @update:options="loadItems"
      >
        <template v-slot:item.date="{ item }">
          <b v-if="item._totals">Total</b>
          <span v-else>{{ getDateUserFullFormat(item.date) }}</span>
        </template>
        <template v-slot:item.amount="{ item }">
          {{ item.amount != null ? item.amount.toFixed(2) + ' €' : '' }}
        </template>
        <template v-slot:item.referrerToReceive="{ item }">
          {{ item.referrerToReceive != null ? item.referrerToReceive.toFixed(2) + ' €' : '' }}
        </template>
        <template v-slot:item.fee="{ item }">
          {{ item.fee != null ? item.fee.toFixed(2) + ' €' : '' }}
        </template>
        <template v-slot:item.income="{ item }">
          {{ item.income != null ? item.income.toFixed(2) + ' €' : '' }}
        </template>
        <template v-slot:item.iva="{ item }">
          {{ item.iva != null ? item.iva.toFixed(2) + ' €' : '' }}
        </template>
        <template v-slot:item.net="{ item }">
          {{ item.net != null ? item.net.toFixed(2) + ' €' : '' }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import FileSaver from 'file-saver'
import { adminGetStripePayments, adminGetStripePaymentsCsv } from '@/lib/api.js'
import { DateTime } from 'luxon'
import tableMixin from '@/views/components/core/tableMixin.js'
import { getDateUserFullFormat } from '@/lib/utils/date.js'
import ExportButton from '@/views/components/common/ExportButton'

export default {
  components: {
    ExportButton,
  },
  mixins: [tableMixin],
  data() {
    return {
      loading: false,
      exportLoading: false,
      totals: null,
      filters: {
        dateFrom: DateTime.now().minus({ days: 30 }).toFormat('yyyy-MM-dd'),
        dateTo: DateTime.now().toFormat('yyyy-MM-dd'),
        userReferralCode: null,
        referrerUserId: null,
      },
    }
  },
  computed: {
    defaultHeaders() {
      return [
        { text: 'Fecha', value: 'date' },
        { text: 'User ID', value: 'userId' },
        { text: 'Email', value: 'customerEmail' },
        { text: 'Importe', value: 'amount' },
        { text: 'Código referral', value: 'userReferralCode' },
        { text: 'A pagar referrer', value: 'referrerToReceive' },
        { text: 'Fee', value: 'fee' },
        { text: 'Income', value: 'income' },
        { text: 'IVA', value: 'iva' },
        { text: 'Neto', value: 'net' },
      ]
    },
    tableItems() {
      if (!this.totals) return this.items
      const totalsRow = {
        _totals: true,
        amount: this.totals.amount,
        referrerToReceive: this.totals.referrerToReceive,
        net: this.totals.net,
        iva: this.totals.iva,
      }
      return [totalsRow, ...this.items]
    },
  },
  methods: {
    getDateUserFullFormat,
    getItemClass(item) {
      return item._totals ? 'totals-row font-weight-bold' : ''
    },
    updateFilters() {
      this.setFirstPage()
      this.loadItems()
    },
    buildFilter() {
      const filter = JSON.parse(JSON.stringify(this.filters))
      if (!filter.dateFrom) delete filter.dateFrom
      if (!filter.dateTo) delete filter.dateTo
      if (!filter.userReferralCode) delete filter.userReferralCode
      if (!filter.referrerUserId) delete filter.referrerUserId
      return filter
    },
    getCurrentSort() {
      const { sort } = this.getTablePaginationOptions({
        page: this.options.page || 1,
        itemsPerPage: this.options.itemsPerPage || 20,
        sortBy: this.options.sortBy || ['date'],
        sortDesc: this.options.sortDesc || [true],
      })
      return sort
    },
    async loadItems() {
      this.loading = true
      try {
        const filter = this.buildFilter()

        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await adminGetStripePayments({ filter, page, limit, sort })
        this.setTablePaginatedResponse(response)
        this.totals = response.data.totals
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    async downloadCsv() {
      this.exportLoading = true
      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const result = await adminGetStripePaymentsCsv({
          filter: this.buildFilter(),
          sort: this.getCurrentSort(),
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        FileSaver.saveAs(blob, `Filios - Admin Stripe Payments ${nowFormatted}.csv`)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.exportLoading = false
    },
  },
}
</script>

<style lang="scss">
#admin-stripe-payments {
  .totals-row {
    background-color: rgba(0, 0, 0, 0.05);
  }
}
</style>
